/* import __COLOCATED_TEMPLATE__ from './custom-office-hours.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type OfficeHoursSchedule from 'embercom/models/office-hours-schedule';
import type { OfficeHoursExceptionData } from 'embercom/models/office-hours-schedule';

interface Signature {
  Args: {
    showCustomOfficeHoursEmptyState: boolean;
    createCustomOfficeHours: () => void;
    schedules: OfficeHoursSchedule[];
    openSettingsModal: (schedule: OfficeHoursSchedule) => void;
    attemptDeleteSchedule: (schedule: OfficeHoursSchedule) => void;
  };
}

export default class CustomOfficeHoursTab extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get canUseHolidayOfficeHours(): boolean {
    return this.appService.app.canUseFeature('holiday-office-hours');
  }

  get columns() {
    let columns = [
      {
        label: this.intl.t('components.settings.office-hours.table.columns.name'),
        valuePath: 'name',
        width: '30%',
      },
      {
        label: this.intl.t('components.settings.office-hours.table.columns.time-zone'),
        valuePath: 'timeZone',
      },
      {
        label: this.intl.t('components.settings.office-hours.table.columns.scheduled-hours'),
        valuePath: 'label',
      },
    ];

    if (this.canUseHolidayOfficeHours) {
      columns.push({
        label: this.intl.t('components.settings.office-hours.table.columns.holidays'),
        valuePath: 'exceptions',
      });
    }

    columns.push({
      label: this.intl.t('components.settings.office-hours.table.columns.associated-team-inboxes'),
      valuePath: 'linkedTeamIds',
    });

    return columns;
  }

  @action
  formatExceptions(exceptions: OfficeHoursExceptionData[]): string[] {
    if (!exceptions) {
      return [];
    }
    return exceptions.slice(0, 3).map((exc) => {
      let date = this.formatExceptionDate(exc.exception_date);
      let hours = this.formatExceptionHours(exc);
      return `${date} \u00B7 ${hours}`;
    });
  }

  private formatExceptionDate(dateString: string): string {
    let date = new Date(dateString);
    return date.toLocaleDateString(this.intl.primaryLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  private formatExceptionHours(exception: OfficeHoursExceptionData): string {
    if (exception.exception_type === 'closed') {
      return this.intl.t('components.settings.office-hours.exceptions.type.closed');
    }

    return exception.time_intervals
      .map((interval) => {
        let formatTime = (minutes: number) => {
          let hours = Math.floor(minutes / 60);
          let mins = minutes % 60;
          let period = hours >= 12 ? 'pm' : 'am';
          let displayHours = hours;
          if (hours > 12) {
            displayHours = hours - 12;
          } else if (hours === 0) {
            displayHours = 12;
          }
          return `${displayHours}:${mins.toString().padStart(2, '0')}${period}`;
        };
        return `${formatTime(interval.start_minute)} - ${formatTime(interval.end_minute)}`;
      })
      .join(', ');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::OfficeHours::Tabs::CustomOfficeHours': typeof CustomOfficeHoursTab;
    'new-settings/workspace/office-hours/tabs/custom-office-hours': typeof CustomOfficeHoursTab;
  }
}
